import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles7to18 } from './articles-7-18';
import { articles13to20 } from './articles-13-20';
import { packingGuideArticles } from './articles-packing-guide-2026';
import { wasteDisposalArticles } from './articles-waste-disposal-2026';
import { insuranceClaimsArticles } from './articles-insurance-claims-2026';
import { movingPreparationQAArticles } from './articles-moving-preparation-qa-2026';
import { choosingMovingCompanyQAArticles } from './articles-choosing-moving-company-qa-2026';
import { movingCostQAArticles } from './articles-moving-cost-qa-2026';
import { movingDayQAArticles } from './articles-moving-day-qa-2026';
import { saveMovingCostQAArticles } from './articles-save-moving-cost-qa-2026';
import { howToTutorialArticles } from './articles-howto-tutorials-2026';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SchemaOrg from '../../../components/feature/SchemaOrg';

interface ArticleContent {
  id: number;
  title: string;
  category: string;
  tags: string[];
  readTime: string;
  image: string;
  publishDate: string;
  author: string;
  content: {
    introduction: string;
    sections: {
      title: string;
      content: string[];
    }[];
    conclusion: string;
  };
  relatedArticles: number[];
}

const categories = {
  preparation: '搬家準備',
  packing: '打包技巧',
  transportation: '搬運技巧',
  'special-items': '特殊物品',
  cost: '費用相關',
  tips: '實用技巧',
  seasonal: '季節搬家',
  organization: '整理收納'
};

const articles = {
  1: {
    id: 1,
    title: '搬家前30天完整準備清單，讓搬家不再手忙腳亂',
    category: 'preparation',
    tags: ['搬家準備', '時程規劃', '清單'],
    readTime: '5分鐘',
    image: 'https://public.readdy.ai/ai/img_res/65fe797ebf6164c9aa69326201a4179c.jpg',
    publishDate: '2024年12月20日',
    author: '建鈞精緻搬家編輯團隊',
    content: {
      introduction: '搬家是一項複雜的工程，提前30天開始準備能讓整個過程更順利。本文為您整理完整的搬家準備時程表，從選擇搬家公司到最後的清潔工作，每個步驟都為您詳細說明。',
      sections: [
        {
          title: '搬家前30-21天：初步規劃',
          content: [
            '這個階段是搬家準備的開始，需要做好整體規劃。首先，確定搬家日期，考慮工作安排、租約到期日等因素。接著開始尋找搬家公司，比較至少3家公司的報價和服務內容。',
            '同時開始整理物品，決定哪些要帶走、哪些要丟棄或捐贈。這是進行斷捨離的最佳時機，減少不必要的物品可以降低搬家成本。',
            '如果是租屋，記得提前通知房東搬家日期，確認押金退還事宜。自有房屋則需要開始尋找買家或租客。'
          ]
        },
        {
          title: '搬家前20-14天：物品整理與打包準備',
          content: [
            '開始購買或收集打包材料，包括紙箱、氣泡紙、膠帶、標籤等。可以向超市、書店索取免費紙箱，或購買專業搬家紙箱。',
            '開始打包不常用的物品，如換季衣物、書籍、裝飾品等。每個箱子都要清楚標示內容物和目的地房間，方便搬家後整理。',
            '聯絡水電、瓦斯、網路等公用事業單位，預約停用舊家和啟用新家的時間。同時辦理地址變更通知，包括銀行、保險、政府機關等。'
          ]
        },
        {
          title: '搬家前13-7天：加速打包與細節確認',
          content: [
            '這週要加速打包進度，開始處理廚房、浴室等較複雜的區域。易碎品要特別小心包裝，使用足夠的緩衝材料。',
            '與搬家公司確認最終細節，包括搬家時間、車輛大小、人力配置、特殊物品處理等。確認報價是否包含所有服務項目。',
            '開始清理冰箱，逐漸消耗食物，避免浪費。大型家電如需拆裝，預約專業師傅的時間。'
          ]
        },
        {
          title: '搬家前6-3天：最後衝刺',
          content: [
            '完成所有物品的打包，只留下搬家當天和前一天需要使用的物品。準備一個隨身行李，放入重要文件、貴重物品、換洗衣物等。',
            '再次確認搬家公司的時間和細節，準備好現金支付搬家費用。如果是公寓大樓，預約電梯使用時間，並通知管理員。',
            '開始清潔舊家，特別是廚房和浴室。如果是租屋，徹底清潔可以確保押金全額退還。'
          ]
        },
        {
          title: '搬家當天：執行與監督',
          content: [
            '搬家當天要早起，做好最後的準備工作。搬家公司到達後，先確認物品清單，說明特殊物品的處理方式。',
            '全程監督搬運過程，確保物品被妥善處理。貴重物品和重要文件最好自己攜帶，不要交給搬家公司。',
            '到達新家後，指導搬家人員將物品放置到正確的房間。驗收所有物品，確認沒有損壞或遺失。'
          ]
        },
        {
          title: '搬家後1-3天：整理與適應',
          content: [
            '優先拆箱整理必需品，如寢具、盥洗用品、廚房用品等，確保基本生活機能。',
            '逐步整理其他物品，不要急於一次完成。可以先將箱子放在對應的房間，再慢慢整理。',
            '熟悉新環境，了解周邊的超市、醫院、學校等生活機能。與新鄰居打招呼，建立良好的鄰里關係。'
          ]
        }
      ],
      conclusion: '搬家是一項需要周密計劃的工程，提前30天開始準備可以讓整個過程更加順利。記住，充分的準備是成功搬家的關鍵。如果覺得自己處理太複雜，不妨考慮委託專業的搬家公司，他們的經驗和專業能讓您的搬家更輕鬆、更安心。'
    },
    relatedArticles: [2, 3, 5]
  }
};

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState<any>(null);

  // 合併所有文章數據
  const allArticles = useMemo(() => ({
    ...articles,
    ...articles7to18,
    ...articles13to20,
    ...packingGuideArticles,
    ...wasteDisposalArticles,
    ...insuranceClaimsArticles,
    ...movingPreparationQAArticles,
    ...choosingMovingCompanyQAArticles,
    ...movingCostQAArticles,
    ...movingDayQAArticles,
    ...saveMovingCostQAArticles,
    ...howToTutorialArticles
  }), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const articleId = parseInt(id || '1');
    
    // 獲取文章數據
    const articleData = allArticles[articleId as keyof typeof allArticles];
    
    setArticle(articleData);
  }, [id, allArticles]);

  // 計算側邊欄相關文章（基於標籤相似度）
  const sidebarRelatedArticles = useMemo(() => {
    if (!article) return [];
    
    const currentTags = article.tags || [];
    const currentId = article.id;
    
    // 計算每篇文章與當前文章的相關度
    const articlesWithScore = Object.values(allArticles)
      .filter((a: any) => a.id !== currentId)
      .map((a: any) => {
        const articleTags = a.tags || [];
        // 計算標籤重疊數量
        const commonTags = currentTags.filter((tag: string) => articleTags.includes(tag));
        // 同分類加分
        const categoryBonus = a.category === article.category ? 2 : 0;
        return {
          ...a,
          score: commonTags.length + categoryBonus
        };
      })
      .filter((a: any) => a.score > 0)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 5);
    
    return articlesWithScore;
  }, [article, allArticles]);

  // 獲取熱門標籤
  const popularTags = useMemo(() => {
    const tagCount: Record<string, number> = {};
    Object.values(allArticles).forEach((a: any) => {
      (a.tags || []).forEach((tag: string) => {
        tagCount[tag] = (tagCount[tag] || 0) + 1;
      });
    });
    return Object.entries(tagCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([tag]) => tag);
  }, [allArticles]);

  // SEO Schema
  useEffect(() => {
    if (!article) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sadiest018moving.com';
    
    // 將中文日期格式轉換為 ISO 格式
    const parseChineseDate = (dateStr: string): string => {
      const match = dateStr.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
      if (match) {
        const [, year, month, day] = match;
        return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`).toISOString();
      }
      return new Date().toISOString();
    };
    
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.content.introduction,
      image: article.image,
      datePublished: parseChineseDate(article.publishDate),
      dateModified: parseChineseDate(article.publishDate),
      author: {
        '@type': 'Person',
        name: article.author,
        url: siteUrl
      },
      publisher: {
        '@type': 'Organization',
        name: '建鈞精緻搬家',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`,
          width: 600,
          height: 60
        },
        url: siteUrl,
        telephone: '0978-900-081',
        priceRange: '$$',
        image: 'https://readdy.ai/api/search-image?query=Professional%20moving%20company%20team%20with%20modern%20moving%20truck%20and%20equipment%20in%20front%20of%20office%20building%2C%20clean%20professional%20appearance%20with%20blue%20and%20white%20branding%2C%20bright%20daylight%20photography&width=1200&height=630&seq=company-image&orientation=landscape',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '新北市泰山區',
          addressLocality: '新北市',
          addressRegion: '新北市',
          postalCode: '243',
          addressCountry: 'TW'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/knowledge/article/${id}`
      },
      articleSection: categories[article.category as keyof typeof categories],
      keywords: article.tags.join(', '),
      wordCount: article.content.sections.reduce((total: number, section: any) => 
        total + section.content.join(' ').length, 0
      ),
      inLanguage: 'zh-TW',
      about: {
        '@type': 'Thing',
        name: '搬家服務'
      }
    };

    const scriptId = `article-schema-${id}`;
    let scriptElement = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (scriptElement) {
      scriptElement.textContent = JSON.stringify(articleSchema);
    } else {
      scriptElement = document.createElement('script');
      scriptElement.type = 'application/ld+json';
      scriptElement.id = scriptId;
      scriptElement.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(scriptElement);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script && script.parentNode === document.head) {
        try {
          document.head.removeChild(script);
        } catch (e) {
          // 忽略錯誤
        }
      }
    };
  }, [id, article]);

  // Breadcrumb Schema
  useEffect(() => {
    if (!article) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sadiest018moving.com';
    
    // SEO 配置
    document.title = `${article.title} - 建鈞精緻搬家`;
    
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', article.content.introduction.substring(0, 160));
    updateMetaTag('keywords', article.tags.join(','));

    // Breadcrumb Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首頁',
          item: `${siteUrl}/`
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '搬家知識',
          item: `${siteUrl}/knowledge`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `${siteUrl}/knowledge/article/${id}`
        }
      ]
    };

    const breadcrumbScriptId = `breadcrumb-schema-${id}`;
    let breadcrumbScript = document.getElementById(breadcrumbScriptId) as HTMLScriptElement;
    
    if (breadcrumbScript) {
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    } else {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.id = breadcrumbScriptId;
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }

    return () => {
      const script = document.getElementById(breadcrumbScriptId);
      if (script && script.parentNode === document.head) {
        try {
          document.head.removeChild(script);
        } catch (e) {
          // 忽略錯誤
        }
      }
    };
  }, [id, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">文章不存在</h2>
          <p className="text-gray-600 mb-8">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link
            to="/knowledge"
            className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            返回知識中心
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticlesData = article.relatedArticles
    .map((relatedId: number) => {
      const related = allArticles[relatedId as keyof typeof allArticles];
      if (!related) return null;
      return {
        id: related.id,
        title: related.title,
        category: related.category,
        readTime: related.readTime,
        image: related.image
      };
    })
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Schema.org 結構化資料 */}
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: article.author,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          url: `https://readdy.ai/knowledge/article?id=${article.id}`
        }}
      />

      {/* HowTo Schema（如果文章有 howToSchema 資料） */}
      {article.howToSchema && (
        <SchemaOrg
          type="HowTo"
          data={article.howToSchema}
        />
      )}

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首頁',
                item: 'https://readdy.ai/'
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '搬家知識',
                item: 'https://readdy.ai/knowledge'
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://readdy.ai/knowledge/article?id=${article.id}`
              }
            ]
          })
        }}
      />

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Article Content */}
          <article className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-teal-100 text-teal-800 text-sm font-medium rounded-full">
                  {categories[article.category as keyof typeof categories]}
                </span>
                <span className="text-gray-500 text-sm flex items-center">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
                <span className="text-gray-500 text-sm flex items-center">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.publishDate}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <i className="ri-user-line mr-2"></i>
                  <span>{article.author}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag: string, index: number) => (
                  <Link
                    key={index}
                    to={`/knowledge/tags?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-teal-100 hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Featured Image */}
            <div className="mb-12 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 lg:h-96 object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed m-0">
                  {article.content.introduction}
                </p>
              </div>

              {article.content.sections.map((section: any, index: number) => (
                <div key={index} className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-600">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.content.map((paragraph: string, pIndex: number) => (
                      <p key={pIndex} className="text-gray-700 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 p-8 rounded-xl mt-12">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-lightbulb-flash-line text-teal-600 mr-2"></i>
                  總結
                </h3>
                <p className="text-gray-700 leading-relaxed m-0">
                  {article.content.conclusion}
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-12 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">需要專業的搬家服務？</h3>
              <p className="text-teal-100 mb-6">
                我們提供專業、安全、快速的搬家服務，讓您的搬家過程輕鬆無憂
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link
                  to="/quote"
                  className="inline-flex items-center px-6 py-3 bg-white text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line mr-2"></i>
                  免費估價
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  聯絡我們
                </Link>
              </div>
            </div>

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h4 className="text-lg font-semibold text-gray-900">分享這篇文章</h4>
                <div className="flex gap-3">
                  <button 
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                    className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-fill"></i>
                  </button>
                  <a 
                    href="https://line.me/ti/p/~sarahst018" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
                  >
                    <i className="ri-line-fill"></i>
                  </a>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('連結已複製到剪貼簿！');
                    }}
                    className="w-10 h-10 flex items-center justify-center bg-gray-700 text-white rounded-full hover:bg-gray-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-links-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* 相關文章側邊欄 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-article-line mr-2 text-teal-600"></i>
                  相關文章
                </h3>
                <div className="space-y-4">
                  {sidebarRelatedArticles.length > 0 ? (
                    sidebarRelatedArticles.map((related: any) => (
                      <Link
                        key={related.id}
                        to={`/knowledge/article/${related.id}`}
                        className="flex gap-3 group"
                      >
                        <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-2 leading-tight">
                            {related.title}
                          </h4>
                          <span className="text-xs text-gray-500 mt-1 flex items-center">
                            <i className="ri-time-line mr-1"></i>
                            {related.readTime}
                          </span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    relatedArticlesData.slice(0, 5).map((related: any) => (
                      <Link
                        key={related.id}
                        to={`/knowledge/article/${related.id}`}
                        className="flex gap-3 group"
                      >
                        <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-2 leading-tight">
                            {related.title}
                          </h4>
                          <span className="text-xs text-gray-500 mt-1 flex items-center">
                            <i className="ri-time-line mr-1"></i>
                            {related.readTime}
                          </span>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
                <Link
                  to="/knowledge"
                  className="mt-4 flex items-center justify-center text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                >
                  查看更多文章
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>

              {/* 熱門標籤 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-price-tag-3-line mr-2 text-teal-600"></i>
                  熱門標籤
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/knowledge/tags?tag=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-teal-100 hover:text-teal-700 transition-colors cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>

              {/* 快速諮詢卡片 */}
              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-6 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-4">
                  <i className="ri-customer-service-2-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold mb-2">需要搬家協助？</h3>
                <p className="text-teal-100 text-sm mb-4">
                  專業團隊為您提供免費諮詢與估價服務
                </p>
                <div className="space-y-3">
                  <a
                    href="tel:0978900081"
                    className="flex items-center justify-center w-full px-4 py-2.5 bg-white text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    0978-900-081
                  </a>
                  <a
                    href="https://line.me/ti/p/~sarahst018"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full px-4 py-2.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-line-fill mr-2"></i>
                    LINE 諮詢
                  </a>
                </div>
              </div>

              {/* 大家都在問 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-questionnaire-line mr-2 text-teal-600"></i>
                  大家都在問
                </h3>
                <div className="space-y-3">
                  {[
                    { id: 104, title: '搬家前要準備什麼？' },
                    { id: 105, title: '搬家公司怎麼選？' },
                    { id: 106, title: '搬家要多少錢？' },
                    { id: 107, title: '搬家當天要注意什麼？' },
                    { id: 108, title: '搬家怎麼省錢？' },
                  ].map((item) => (
                    <Link
                      key={item.id}
                      to={`/knowledge/article/${item.id}`}
                      className="flex items-center text-sm text-gray-700 hover:text-teal-600 transition-colors group"
                    >
                      <i className="ri-question-line mr-2 text-gray-400 group-hover:text-teal-500"></i>
                      <span className="line-clamp-1">{item.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related Articles - Bottom Section */}
      {relatedArticlesData.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
              <i className="ri-article-line mr-3 text-teal-600"></i>
              延伸閱讀
            </h3>
            <div className="grid gap-6 md:grid-cols-3">
              {relatedArticlesData.map((related: any) => (
                <Link
                  key={related.id}
                  to={`/knowledge/article/${related.id}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-teal-600 text-white text-xs font-medium rounded-full">
                        {categories[related.category as keyof typeof categories]}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <div className="flex items-center text-sm text-gray-500">
                      <i className="ri-time-line mr-2"></i>
                      <span>閱讀時間 {related.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Back to Knowledge Center */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/knowledge"
          className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          返回搬家知識中心
        </Link>
      </div>

      <Footer />
    </div>
  );
}